import {useDispatch, useSelector} from "react-redux";
import React, {useState, useEffect} from "react";
import * as projectActions from "../../../redux/actions/ProjectsActions";
import * as datasetActions from "../../../redux/actions/DataSetActions";
import * as workflowActions from "../../../redux/actions/WorkflowActions";
import WorkflowList from "components/Workflows/WorkflowList/WorkflowList";
import {ENTITY_TYPES, PAGE_SIZE_CONSTANTS} from "../../../utility/constants/constants";
import { getHighestAccessLevel } from "utility/utility";
import {Row} from "../Row/Row";
import PaginationComponent from "../Pagination/Pagination";
import EmptyScreen from "components/shared/ListPage/emptyScreen";
import FilterInsights from "../../Insights/InsightList/FilterInsights";
import { ShareEntityModal } from "../Modals/ShareEntityModal/ShareEntityModal";

const EntityList = ({entityName, entityType, user, filterParams, setFilterParams}) => {
  const dispatch = useDispatch();

  const [shareTarget, setShareTarget] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleShare = (entity, entityType) => {
    setShareTarget({ entityId: entity.id, entityType });
    setShowShareModal(true);
  };

  const handleShareWorkflow = (workflow) => {
    setShareTarget({ entityId: workflow.id, entityType: ENTITY_TYPES.WORKFLOW });
    setShowShareModal(true);
  };

  const {projects, projectsPageCount} = useSelector((state) => state.projectsReducer);
  const {datasets, datasetsPageCount} = useSelector((state) => state.dataSetReducer);
  const {workflows, workflowsPageCount} = useSelector((state) => state.workflowReducer);

  let pageCount = 0
  const entityFilterOptions = [
    { value: "All", label: <span className="font-size-normal">All</span>},
    { value: "Created by me", label: <span  className="font-size-normal">Created by me</span> },
    { value: "Shared with me", label: <span  className="font-size-normal">Shared with me</span> }
  ];

  if (entityType === ENTITY_TYPES.PROJECT_V2) {
    pageCount = projectsPageCount
  } else if (entityType === ENTITY_TYPES.DATASET) {
    pageCount = datasetsPageCount
  } else if (entityType === ENTITY_TYPES.WORKFLOW) {
    pageCount = workflowsPageCount
  }

  useEffect(() => {
    if (entityType === ENTITY_TYPES.PROJECT_V2) {
      filterParams.pageSize = PAGE_SIZE_CONSTANTS.ROWS
      dispatch(projectActions.getProjects(filterParams));
    } else if (entityType === ENTITY_TYPES.DATASET) {
      filterParams.pageSize = PAGE_SIZE_CONSTANTS.ROWS
      dispatch(datasetActions.getDataSets(filterParams))
    } else if (entityType === ENTITY_TYPES.WORKFLOW) {
      filterParams.pageSize = PAGE_SIZE_CONSTANTS.WORKFLOWS
      dispatch(workflowActions.getWorkflows(filterParams)).then((res) => {
        dispatch(workflowActions.getWorkflowData({ body: { workflows: res.value.workflows } }))
      });
    }
  }, [dispatch, entityType, filterParams])

  // return to page 1 when a new entity list is selected from a current entity list
  useEffect(() => {
    setFilterParams((prev) => ({ ...prev, page: 1 }));
  }, [entityType, setFilterParams]);

  return (
    <>
      <div className={"lds_section_header"} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <h5>My {entityName}</h5>
        {filterParams && setFilterParams && (
          <FilterInsights
            appTypeSort={filterParams}
            setAppTypeSort={setFilterParams}
            entityFilterOptions={entityFilterOptions}
          />
        )}
      </div>
      <div className="row">
        <div className="col-sm-12">
          {
            projects && projects.length > 0 && entityType === ENTITY_TYPES.PROJECT_V2 ? (
              projects.map((project) => {
                return (
                  <Row
                    entity={project}
                    entityType={ENTITY_TYPES.PROJECT_V2}
                    accessLevel={getHighestAccessLevel(project, user)}
                    isSharedWithMe={false}
                    onShare={handleShare}
                    key={project.id}
                  />
                )
              })
            ) : null
          }
          {
            projects && projects.length === 0 && entityType === ENTITY_TYPES.PROJECT_V2 ? (
              <div className={"lds_empty_state"}>
                <EmptyScreen entity={ENTITY_TYPES.PROJECT_V2}></EmptyScreen>
              </div>
            ) : null
          }
          {
            datasets && datasets.length > 0 && entityType === ENTITY_TYPES.DATASET ? (
              datasets.map((dataset) => {
                if (dataset?.owner_id) {
                  return (
                    <Row
                      entity={dataset}
                      entityType={ENTITY_TYPES.DATASET}
                      accessLevel={getHighestAccessLevel(dataset, user)}
                      isSharedWithMe={false}
                      onShare={handleShare}
                      key={dataset.id}
                    />
                  )
                } else {
                  return null
                }
              })
            ) : null
          }
          {
            datasets && datasets.length === 0 && entityType === ENTITY_TYPES.DATASET ? (
              <div className={"lds_empty_state"}>
                <EmptyScreen entity={ENTITY_TYPES.DATASET}></EmptyScreen>
              </div>
            ) : null
          }
          {
            workflows && workflows.length > 0 && entityType === ENTITY_TYPES.WORKFLOW ? (
              <WorkflowList filterParams={filterParams} onShare={handleShareWorkflow}/>
            ) : null
          }
          {
            workflows && workflows.length === 0 && entityType === ENTITY_TYPES.WORKFLOW ? (
              <div className={"lds_empty_state"}>
                <EmptyScreen entity={ENTITY_TYPES.WORKFLOW}></EmptyScreen>
              </div>
            ) : null
          }
        </div>
      </div>
      <div className="pagination_container">
        <div className="entity_pagination">
          <PaginationComponent
            setNextPage={() => setFilterParams({...filterParams, page: filterParams.page + 1})}
            setLastPage={() => setFilterParams({...filterParams, page: filterParams.page - 1})}
            page={filterParams.page}
            count={pageCount}
          />
        </div>
      </div>
      {shareTarget && (
        <ShareEntityModal
          entityId={shareTarget.entityId}
          entityType={shareTarget.entityType}
          showModal={showShareModal}
          setShowModal={setShowShareModal}
          onSuccess={() => {
            if (entityType === ENTITY_TYPES.WORKFLOW) {
              dispatch(workflowActions.getWorkflows(filterParams)).then((res) => {
                dispatch(workflowActions.getWorkflowData({ body: { workflows: res.value.workflows } }));
              });
            } else if (entityType === ENTITY_TYPES.PROJECT_V2) {
              dispatch(projectActions.getProjects(filterParams));
            } else if (entityType === ENTITY_TYPES.DATASET) {
              dispatch(datasetActions.getDataSets(filterParams));
            }
          }}
        />
      )}
    </>
  )
}

export default EntityList;
